<template>
  <v-img
    :src="'/media/' + image.image.id"
    :lazy-src="JSON.parse(image.image.metaData)['image-lazy-src']"
    :aspect-ratio="aspectRatio"
    :max-height="maxHeight"
    :max-width="maxWidth"
    :height="height"
    :width="width"
    :contain="contain"
    :cover="cover"
    :eager="eager"
    transition="project-photo-transition"
    class="project-seo-image"
    :class="{loaded: loaded}"
    @load="loaded = true"
    @error="loaded = true"
    @click.native="$emit('update:openCaptionID', '')"
    >
    <!-- loading -->
    <template v-slot:placeholder>
      <div :style="getImageOverlayStyle(image.image)" v-if="!fullWidthCaption" >
        <v-progress-linear indeterminate color="rgba(255, 255, 255, 0.2)" :background-opacity="0" :height="2" />
      </div>
    </template>

    <div class="fill-height" :style="getImageOverlayStyle(image.image)" v-if="loaded">
      <!-- caption closed -->
      <div v-if="openCaptionID !== id" @click.stop.prevent="$emit('update:openCaptionID', id)"  class="fill-height">
        <IconCross width="17" height="17" class="open-button" />
      </div>

      <!-- caption open -->
      <transition name="fade" appear>
        <div v-if="openCaptionID === id" class="image-caption-popup" @click.stop.prevent="$emit('update:openCaptionID', '')">
            <v-row class="fill-height" align="end" justify="center">
              <v-col cols="10">
                <IconCross class="mr-n4 mt-0 close-icon float-right" @click="$emit('update:openCaptionID', '')" width="17" height="17" />
                <h4>{{author}}</h4>
                <MarkdownBlock :inline="false" linkify tag="div" :text="image.caption" class="caption"/>
              </v-col>
            </v-row>
        </div>
      </transition>
    </div>
  </v-img>
</template>

<script>
import { Viewport, MarkdownBlock } from '@monogrid/vue-lib'
import IconCross from '@/assets/svg/icon-caption.svg'
import getAspect from '@/mixins/getAspect'

export default {
  name: 'ProjectImage',

  mixins: [Viewport, getAspect],

  components: {
    IconCross,
    MarkdownBlock
  },

  props: {
    id: {
      type: String,
      required: true
    },
    openCaptionID: {
      type: String,
      required: true
    },
    image: {
      type: Object,
      required: true
    },
    author: {
      type: String,
      required: true
    },
    fullWidthCaption: {
      type: Boolean,
      required: false,
      default: false
    },
    aspectRatio: { required: false },
    maxHeight: { required: false },
    maxWidth: { required: false },
    height: { required: false },
    width: { required: false },
    contain: { required: false },
    cover: { required: false },
    eager: { required: false }
  },

  data () {
    return {
      loaded: false
    }
  },

  methods: {
    // async onContainerClick () {
    //   await this.$nextTick()
    //   if (this.openCaptionID === this.id) { this.$emit('update:openCaptionID', '') }
    // },
    getImageOverlayStyle (image) {
      if (this.fullWidthCaption) {
        return {
          width: '100%',
          height: '100%'
        }
      }
      const imageAspect = this.getAspect(image)
      const screenAspect = this.viewPort.width / this.viewPort.height
      let w
      let h
      if (imageAspect <= screenAspect) {
        w = `${Math.ceil((this.viewPort.height * 0.8) * imageAspect)}px`
        h = '100%'
      } else {
        w = '100%'
        h = '100%'
      }

      return {
        width: w,
        height: h,
        margin: '0 auto',
        position: 'relative'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$progress-bar-color: rgba(255, 255, 255, 0.2) !default;

.open-button {
  position: absolute;

  right: 10px;
  bottom: -25px;

  cursor: pointer;
  pointer-events: all;

  svg {
    width: 17px;
    height: 17px;
  }
}

.image-caption-popup {
  position: absolute;

  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  background-color: rgba(0, 0, 0, 0.8);

  .col {
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    // testing scroll issue in caption
    // ::v-deep p { // stylelint-disable-line
    //   font-size: 30px !important;
    // }
  }

  p,
  h4 {
    font-size: 18px;
    font-family: Barlow, sans-serif;
  }

  svg {
    cursor: pointer;
    width: 20px;
    height: 20px;
    transform-origin: center;
    transform: rotate(45deg);

    path {
      stroke: $c-grey !important;
    }
  }
}

.project-seo-image {
  overflow: visible !important;
}

// Progress Bar
.progress {
  position: relative;

  display: block;
  width: 100%;
  // background-color: lighten($progress-bar-color, 40%);
  // border-radius: 2px;
  background-clip: padding-box;
  // margin: 0.5rem 0 1rem 0;
  overflow: hidden;

  .indeterminate {
    height: 2px;

    &::before {
      content: '';
      position: absolute;
      background-color: $progress-bar-color;
      top: 0;
      left: 0;
      bottom: 0;
      will-change: left, right;
      height: 2px;
      // Custom bezier
      animation: indeterminate 2.1s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite;

    }

    &::after {
      content: '';
      position: absolute;
      background-color: $progress-bar-color;
      top: 0;
      left: 0;
      bottom: 0;
      will-change: left, right;
      height: 2px;
      // Custom bezier
      animation: indeterminate-short 2.1s cubic-bezier(0.165, 0.84, 0.44, 1) infinite;
      animation-delay: 1.15s;
    }
  }
}

@keyframes indeterminate {
  0% {
    left: -35%;
    right: 100%;
  }

  60% {
    left: 100%;
    right: -90%;
  }

  100% {
    left: 100%;
    right: -90%;
  }
}

@keyframes indeterminate-short {
  0% {
    left: -200%;
    right: 100%;
  }

  60% {
    left: 107%;
    right: -8%;
  }

  100% {
    left: 107%;
    right: -8%;
  }
}

</style>
